import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage, portalApi } from '../api/client';
import type { Balances, KybDocumentsMap, MeResponse, MerchantProfile, PortalUser } from '../api/types';
import { EMPTY_KYB_DOCUMENTS } from '../api/types';
import { PENDING_2FA_KEY, TOKEN_STORAGE_KEY } from '../config';

interface AuthContextValue {
  user: PortalUser | null;
  merchant: MerchantProfile | null;
  kybDocuments: KybDocumentsMap;
  balances: Balances | null;
  financialLocked: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setToken: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const sessionEpochRef = useRef(0);
  const [user, setUser] = useState<PortalUser | null>(null);
  const [merchant, setMerchant] = useState<MerchantProfile | null>(null);
  const [kybDocuments, setKybDocuments] = useState<KybDocumentsMap>(EMPTY_KYB_DOCUMENTS);
  const [balances, setBalances] = useState<Balances | null>(null);
  const [financialLocked, setFinancialLocked] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyMe = useCallback((data: MeResponse) => {
    setUser(data.user);
    setMerchant(data.merchant);
    setKybDocuments(data.kyb_documents ?? EMPTY_KYB_DOCUMENTS);
    setBalances(data.balances);
    setFinancialLocked(data.financial_operations_locked);
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!token) {
      setUser(null);
      setMerchant(null);
      setKybDocuments(EMPTY_KYB_DOCUMENTS);
      setBalances(null);
      setLoading(false);
      return;
    }
    const epoch = sessionEpochRef.current;
    setLoading(true);
    try {
      const data = await portalApi.me();
      if (epoch !== sessionEpochRef.current) return;
      if (!localStorage.getItem(TOKEN_STORAGE_KEY)) return;
      if (data.ok) applyMe(data);
    } catch (e) {
      if (epoch !== sessionEpochRef.current) return;
      setError(getApiErrorMessage(e));
      if (!localStorage.getItem(TOKEN_STORAGE_KEY)) {
        setUser(null);
        setMerchant(null);
        setBalances(null);
      }
    } finally {
      if (epoch === sessionEpochRef.current) setLoading(false);
    }
  }, [applyMe]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setToken = useCallback(
    (token: string) => {
      localStorage.setItem(TOKEN_STORAGE_KEY, token);
      localStorage.removeItem(PENDING_2FA_KEY);
      void refresh();
    },
    [refresh],
  );

  const logout = useCallback(() => {
    sessionEpochRef.current += 1;
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(PENDING_2FA_KEY);
    setUser(null);
    setMerchant(null);
    setKybDocuments(EMPTY_KYB_DOCUMENTS);
    setBalances(null);
    setFinancialLocked(true);
    setLoading(false);
    setError(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const value = useMemo(
    () => ({ user, merchant, kybDocuments, balances, financialLocked, loading, error, refresh, setToken, logout }),
    [user, merchant, kybDocuments, balances, financialLocked, loading, error, refresh, setToken, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
